import axios from 'axios';
import signupServices from './services';
import i18n, { routeLocale } from '@/i18n';
import { trackPropositionCreated } from '@/utils/mixpanel';
import events from '@/utils/helpers/events';
import { splitStreetAddress } from '@/utils/helpers/residence';
import { createAddressAutocomplete } from '@/utils/addressAutocomplete';
import { router } from '@/main';
import { addDataLayer } from '@/utils/gtm';
import {
  getStoredUtmParams,
  clearUtmParams,
  SEND_UTM_CAPTURED_EVENT
} from '@/utils/utm';

let getPolygonsTimeout = null;

// The address search's Google session (sessionToken, debounce, minimum term
// length) for the whole signup flow. The token is created on the first
// keystroke in the address field and dropped by /place (a picked
// suggestion), a cleared field or resetAddressSearch (the field unmounts).
const addressSearch = createAddressAutocomplete({
  getBaseUrl: () => `${i18n.t('url_geolocation', routeLocale)}/google`
});

const setSwapType = (context, { swapType }) => {
  context.commit('setSwapType', { swapType });
};

const setWishMinRooms = (context, { rooms, wishIndex }) => {
  context.commit('setWishMinRooms', { rooms, wishIndex });
};
const setWishMinSize = (context, { size, wishIndex }) => {
  context.commit('setWishMinSize', { size, wishIndex });
};
const setWishMaxRent = (context, { rent, wishIndex }) => {
  context.commit('setWishMaxRent', { rent, wishIndex });
};
const setResidenceRooms = (context, { rooms, residenceIndex }) => {
  context.commit('setResidenceRooms', { rooms, residenceIndex });
};
const setResidenceAddress = (context, { address, residenceIndex }) => {
  context.commit('setResidenceAddress', { address, residenceIndex });
};
const setResidenceCoordinates = (context, { coordinates, residenceIndex }) => {
  context.commit('setResidenceCoordinates', { coordinates, residenceIndex });
};
const setResidenceNumber = (context, { number, residenceIndex }) => {
  context.commit('setResidenceNumber', { number, residenceIndex });
};
const setResidenceArea = (context, { area, residenceIndex }) => {
  context.commit('setResidenceArea', { area, residenceIndex });
};
const setResidenceFloor = (context, { floor, residenceIndex }) => {
  context.commit('setResidenceFloor', { floor, residenceIndex });
};
const setResidenceSqm = (context, { sqm, residenceIndex }) => {
  context.commit('setResidenceSqm', { sqm, residenceIndex });
};
const setResidenceRent = (context, { rent, residenceIndex }) => {
  context.commit('setResidenceRent', { rent, residenceIndex });
};
const setResidenceType = (context, { type, residenceIndex }) => {
  context.commit('setResidenceType', { type, residenceIndex });
};

const setResidenceZipCode = (context, { zipCode, residenceIndex }) => {
  context.commit('setResidenceZipCode', { zipCode, residenceIndex });
};

const setSwapTimeFrame = (context, timeFrame) => {
  context.commit('setSwapTimeFrame', timeFrame);
};

const setName = (context, { name }) => {
  context.commit('setName', { name });
};

const setSSOProfile = (context, ssoProfile) => {
  context.commit('setSSOProfile', ssoProfile);
};

const setEmail = (context, { email }) => {
  const isValid = signupServices.validateEmail(email);
  context.commit('setSignupError', { error: null });
  context.commit('setEmail', { email, isValid });
};

const USER_SYNC_RETRY_DELAY_MS = 150;
const USER_SYNC_MAX_ATTEMPTS = 20;

async function waitForUserInitiated(context) {
  // app/user is {} until checkIfAuthenticated has fetched the user, so
  // presence of userId — not a null check — is what proves the session
  // was established in the browser.
  for (let attempt = 0; attempt < USER_SYNC_MAX_ATTEMPTS; attempt++) {
    if (context.rootGetters['app/user']?.userId) {
      return true;
    }

    await new Promise(resolve => setTimeout(resolve, USER_SYNC_RETRY_DELAY_MS));
    await context.dispatch('app/initAppData', false, { root: true });
  }

  return Boolean(context.rootGetters['app/user']?.userId);
}

// POST /api/user answers an existing account (email, or Google/Apple) with
// one of these messages; SignupHandler.cs in lagenhetsbyte.se.
const ACCOUNT_EXISTS_MESSAGES = [
  'EMAIL_ALREADY_EXISTS',
  'ACCOUNT_ALREADY_EXISTS'
];

async function register(context) {
  context.commit('setSignupError', { error: null });
  context.commit('setLoading', { loading: true });

  const { email, name, ssoProfile } = context.state;
  try {
    const { status, message } = await signupServices.registerUser(
      email,
      name,
      ssoProfile
    );

    if (status !== 200) {
      if (!ACCOUNT_EXISTS_MESSAGES.includes(message)) {
        // A server error, an invalid email or no answer at all: not a
        // reason to send a new member to the login page.
        context.dispatch(
          'toast/setToast',
          { title: i18n.t('toast_general_error_message') },
          { root: true }
        );
        context.commit('setLoading', { loading: false });
        return false;
      }

      // An existing account (Google/Apple included) is never logged in
      // from here: the signup flow would go on to replace that account's
      // ad. The account step links to the login page instead.
      context.commit('setSignupError', {
        error: i18n.t('signup_error_email_already_exists')
      });
      context.commit('setLoading', { loading: false });
      events.emitEvent(events.eventTypes.SEE, 'Registration Error', {
        userAgent: navigator.userAgent
      });
      return false;
    }

    if (message) {
      window._token = message;
    }

    await context.dispatch('app/initAppData', false, { root: true });

    // Sometimes it takes a little while for the user to get synced via db-sync
    // So for future experiments to work we need to wait for the user sync before continuing
    const userInitiated = await waitForUserInitiated(context);

    if (!userInitiated) {
      // The account was created but no session reached the browser (e.g.
      // the Set-Cookie was rejected). Stop here with a visible error
      // instead of letting the user finish the whole flow on the JWAUTH
      // header alone and get logged out at the last step. Toast, not
      // setSignupError — the account step shows signupError as
      // email-already-exists.
      context.dispatch(
        'toast/setToast',
        { title: i18n.t('toast_general_error_message') },
        { root: true }
      );
      return false;
    }

    try {
      // GTM tracking
      let user = context.rootGetters['app/user'];
      const isMobile = context.rootGetters['screenSize/isMobile'];
      addDataLayer({
        category: 'account',
        action: 'create',
        userId: user.userId,
        userCreatedAt: user.registeredAt,
        isAuthenticated: 1,
        isMobileUser: isMobile
      });

      // Send UTM params if stored from before signup
      const utmParams = getStoredUtmParams();
      if (utmParams && SEND_UTM_CAPTURED_EVENT) {
        events.emitEvent(events.eventTypes.CLICK, 'UTM Captured', {
          ...utmParams,
          userId: user.userId,
          referrer: document.referrer,
          trigger: 'signup'
        });
        clearUtmParams();
      }
    } catch (error) {
      console.error('Could not track:', error);
    }

    return true;
  } catch (e) {
    console.error(e);
    context.commit('setLoading', { loading: false });
  } finally {
    context.commit('setLoading', { loading: false });
  }
}

async function createProposition(context, uploadImages = false) {
  context.commit('setLoading', { loading: true });

  const { residences, wishes, swapTimeFrame } = context.state;
  const { selectedAreas } = context.state;

  // default to 3
  let st = 3;

  if (swapTimeFrame !== null) {
    st = swapTimeFrame;
  }

  try {
    const res = await signupServices.registerProposition({
      residences,
      wishes,
      st,
      selectedAreas
    });

    const resData = JSON.parse(res.data.message);
    const { isFreeArea, isTrial, isFreemium, propositionId } = resData;

    try {
      let type;

      const user = context.rootGetters['app/user'];

      if (isFreeArea) {
        type = 'free';
      } else if (isTrial) {
        type = 'trial';
      } else if (isFreemium) {
        type = 'freemium';
      } else {
        type = 'paying';
      }

      // GTM tracking
      addDataLayer({
        category: 'proposition',
        action: 'create',
        label: type,
        propositionId: propositionId,
        isFreeArea: isFreeArea
      });

      trackPropositionCreated({ type, userId: user.userId, propositionId });

      addDataLayer({
        category: 'proposition',
        what: 'progress',
        action: 100
      });
    } catch (error) {
      console.error('Could not track:', error);
    }

    context.commit('setPropositionSignup', { proposition: resData });

    console.log('Proposition created:', resData);

    if (uploadImages) {
      context.commit('setLoading', { loading: false });
      return true;
    }

    context.dispatch('finishSignup', true);
    return true;
  } catch (e) {
    context.commit('setLoading', { loading: false });
    console.error(e);
    return false;
  }
}

async function finishSignup(context) {
  try {
    const { isFreeArea, isTrial, isFreemium, propositionId } =
      context.state.proposition;

    // Refresh app data — awaited so the router guard on the non-public
    // destination route reads fresh auth state instead of racing this.
    await context.dispatch('app/initAppData', null, { root: true });

    // Reset state
    context.commit(
      'signup/resetState',
      { loading: false, proposition: { propositionId: '' } },
      { root: true }
    );

    let isAppRequest = false;

    try {
      isAppRequest = context.rootState.app.isAppRequest;
    } catch (e) {
      console.log('failed to check app request...');
    }

    let queryParam = isAppRequest ? '?isAppRequest=true' : '';

    // Mark this session as fresh-from-signup so the swap list can start the
    // tutorial even if the SWAPLIST_TUTORIAL task hasn't landed in the tasks
    // API yet. Consumed by SwapList2.
    try {
      sessionStorage.setItem('signupTutorialPending', '1');
    } catch (e) {
      // Storage unavailable (private mode) — the task path still applies.
    }

    if (isFreeArea) {
      router.replace(`${i18n.t('path_swap_list', routeLocale)}${queryParam}`);
    } else if (isTrial) {
      router.replace(`${i18n.t('path_trial_info', routeLocale)}${queryParam}`);
    } else if (isFreemium) {
      router.replace(
        `${i18n.t('path_freemium_info', routeLocale)}${queryParam}`
      );
    } else {
      router.replace(
        `${i18n.t(
          'path_payment_proposition',
          routeLocale
        )}/${propositionId}${queryParam}`
      );
    }
  } catch (e) {
    context.commit('setLoading', { loading: false });
    console.error(e);
  }
}

async function performSearch(context) {
  context.commit('setSearchLoading', { loading: true });
  context.commit('setSearchNoResultsFound', { noResults: false });

  const { searchTerm } = context.state;

  const { val, error } = await signupServices.search(searchTerm);

  if (error) {
    //Handle error
    context.commit('setWishAreasSearchResult', { val: [] });
    context.commit('setSearchLoading', { loading: false });
  } else {
    context.commit('setWishAreasSearchResult', { val: val });
    context.commit('setSearchLoading', { loading: false });
    if (val.length === 0) {
      context.commit('setSearchNoResultsFound', { noResults: true });
    }
  }
}

function blurAreaField(context) {
  context.commit('setSearchTerm', { inputValue: '' });
  context.commit('setWishAreasSearchResult', { val: [] });
}

function selectArea(context, { area, wishIndex }) {
  context.commit('addWishArea', { area, wishIndex });
  context.commit('setSearchTerm', { inputValue: '' });
  context.commit('setWishAreasSearchResult', { val: [] });
  context.dispatch('getPolygonsSignup', { wishIndex });
}

async function getPolygonsSignup(context, { wishIndex }) {
  const { selectedAreas } = context.state;
  const areaIds = selectedAreas[wishIndex].map(a => a.id);

  clearTimeout(getPolygonsTimeout);
  getPolygonsTimeout = setTimeout(async () => {
    const { val, error } = await signupServices.getMultiPolygon(areaIds);
    if (error) {
      context.commit('setPolygonSignup', { wishIndex, val: {} });
    } else {
      context.commit('setPolygonSignup', { wishIndex, val });
    }
  }, 200);
}

async function getWishAreaButtons(context) {
  const fetchedCities = await signupServices.getWishAreaButtons();

  if (!fetchedCities.error) {
    context.commit('setWishAreaOptions', { val: fetchedCities.val });
  }
}

function deselectArea(context, { area, wishIndex }) {
  context.commit('removeWishArea', { area, wishIndex });

  context.dispatch('getPolygonsSignup', { wishIndex });
}

function setSearchTerm(context, { inputValue }) {
  context.commit('setSearchTerm', { inputValue });
}

async function getNumberOfApartmentsForRegions(context, wishIndex) {
  if (context.state.selectedAreas[wishIndex - 1].length > 0) {
    const searchResult = await signupServices.getMatchingApartments(
      context.state.selectedAreas[wishIndex - 1].map(a => a.id)
    );
    context.commit(
      'setNumberOfMatchingApartments',
      searchResult.val.meta.numResults
    );
    return;
  }
  context.commit('setNumberOfMatchingApartments', 0);
}

async function loadSuggestionAddressNumber(
  context,
  { number, residenceIndex }
) {
  if (
    !context.state.selectedSuggestions ||
    !context.state.selectedSuggestions[residenceIndex]
  ) {
    return;
  }

  const currentSuggestion = context.state.selectedSuggestions[residenceIndex];
  const { street } = splitStreetAddress(currentSuggestion.name);
  const searchTerm = `${street} ${number} ${currentSuggestion.description}`;
  // Not debounced here (the number input already is); same session token as
  // the typing, handed on to /place below.
  const { predictions, status } = await addressSearch.autocomplete(searchTerm);
  if (status !== 'OK' || predictions.length === 0) {
    return;
  }

  const addresses = predictions.map(searchResult => {
    return {
      id: searchResult.place_id,
      name: searchResult.structured_formatting.main_text,
      description: searchResult.structured_formatting.secondary_text
    };
  });

  context.commit('setSearchNoResultsFound', { noResults: false });
  context.commit('setAddressSearchResults', {
    addresses,
    results: predictions
  });

  context.dispatch(
    'signup/loadSuggestionLocalityDetails',
    {
      residenceIndex,
      placeId: predictions[0].place_id
    },
    { root: true }
  );
}

// The newest searchAddress call, the only one that may stop the spinner
// when its search comes back null.
let addressSearchCalls = 0;

// Debounced 250 ms by the helper, which also skips terms under 3 characters
// (an empty list) and answers ERROR/502 with an empty list. A search comes
// back as null when a newer search overtook it (that one stops the spinner)
// or when a picked suggestion or leaving the field dropped it (nothing left
// to wait for).
async function searchAddress(context, { searchTerm }) {
  const call = ++addressSearchCalls;
  context.commit('setSearchLoading', { loading: true });
  context.commit('setSearchNoResultsFound', { noResults: false });

  const result = await addressSearch.search(searchTerm);
  if (result === null) {
    if (call === addressSearchCalls) {
      context.commit('setSearchLoading', { loading: false });
    }
    return;
  }

  const { predictions, tooShort } = result;
  const addresses = predictions.map(searchResult => {
    return {
      id: searchResult.place_id,
      name: searchResult.structured_formatting.main_text,
      description: searchResult.structured_formatting.secondary_text
    };
  });

  context.commit('setAddressSearchResults', {
    addresses,
    results: predictions
  });
  context.commit('setSearchLoading', { loading: false });
  context.commit('setSearchNoResultsFound', {
    noResults: !tooShort && predictions.length === 0
  });
}

// Throws the session token away without a /place call: the address field
// was left without picking a suggestion.
function resetAddressSearch() {
  addressSearch.reset();
}

async function loadSuggestionLocalityDetails(
  context,
  { residenceIndex, placeId }
) {
  // Carries the typing session's token, and ends that session.
  const res = await addressSearch.place(placeId);
  const { result } = res.data;
  const { address_components } = result;
  const postalCodeComponent = address_components.find(ac =>
    ac.types.includes('postal_code')
  );

  if (postalCodeComponent) {
    const zipCode = postalCodeComponent.long_name.replace(/ /, '');
    context.dispatch(
      'signup/setResidenceZipCode',
      { zipCode, residenceIndex },
      { root: true }
    );
  } else if (result.geometry && result.geometry.location) {
    const { lat, lng } = result.geometry.location;
    const geolocationResponse = await axios.get(
      `${i18n.t(
        'url_geolocation',
        routeLocale
      )}/location/latlng?lat=${lat}&lng=${lng}`
    );
    const geoZipCode = geolocationResponse.data.find(
      r => r.type === 'PostalCode'
    );
    if (geoZipCode) {
      context.dispatch(
        'signup/setResidenceZipCode',
        { zipCode: geoZipCode.name, residenceIndex },
        { root: true }
      );
    }
  }

  const { geometry } = result;
  if (geometry.location) {
    context.commit('setCoordinates', {
      residenceIndex,
      coordinates: [geometry.location.lng, geometry.location.lat]
    });
  } else {
    context.commit('setCoordinates', {
      residenceIndex,
      coordinates: []
    });
  }
}

function setSelectedSuggestion(
  context,
  { selectedSuggestion, residenceIndex }
) {
  context.commit('setSelectedSuggestion', {
    selectedSuggestion,
    residenceIndex
  });
}

function setCurrentStep(context, { step }) {
  context.commit('setCurrentStep', { step });
}

function setSelectedAreas(context, { selectedAreas }) {
  context.commit('setSelectedAreas', { selectedAreas });
}

function setInSignup(context, { inSignup }) {
  context.commit('setInSignup', { inSignup });
}

export default {
  setSwapType,
  setWishMinRooms,
  setWishMinSize,
  setWishMaxRent,
  setResidenceRooms,
  setResidenceCoordinates,
  setResidenceAddress,
  setResidenceZipCode,
  setResidenceNumber,
  setResidenceArea,
  setResidenceFloor,
  setResidenceSqm,
  setResidenceRent,
  setResidenceType,
  createProposition,
  setSwapTimeFrame,
  setName,
  setEmail,
  register,
  finishSignup,
  setSSOProfile,
  performSearch,
  setSearchTerm,
  getWishAreaButtons,
  blurAreaField,
  selectArea,
  getPolygonsSignup,
  deselectArea,
  getNumberOfApartmentsForRegions,
  searchAddress,
  resetAddressSearch,
  loadSuggestionAddressNumber,
  loadSuggestionLocalityDetails,
  setSelectedSuggestion,
  setSelectedAreas,
  setCurrentStep,
  setInSignup
};
